import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Skeleton,
  cn,
  getAvatarColor,
} from "@nexus/ui";
import { getVendorInitials } from "../../lib/vendorInitials";
import {
  formatVendorPriceFrom,
  type VendorCatalogCardModel,
} from "../../lib/vendor-catalog-model";
import type { MarketplaceSearch } from "@/routes/_authenticated/marketplace.index";

export interface VendorCatalogCardProps {
  card: VendorCatalogCardModel;
  search: MarketplaceSearch;
  onConnect: (vendorId: string) => void;
  onManage: (vendorId: string) => void;
  onSendScripts: (vendorId: string) => void;
}

export function VendorCatalogCard({
  card,
  search,
  onConnect,
  onManage,
  onSendScripts,
}: VendorCatalogCardProps) {
  const { t } = useTranslation();
  const { vendor, availability, isConnected } = card;
  const paused = availability === "paused";
  const unavailable = availability === "unavailable";
  const canConnect = !isConnected && !paused && !unavailable;

  return (
    <Card
      data-vendor-id={vendor.id}
      className={cn(
        "relative flex h-full min-h-[248px] flex-col gap-0 overflow-hidden py-0",
        "transition-[box-shadow,border-color,background-color] duration-150 ease-out",
        "hover:border-primary/25 hover:shadow-sm",
        paused && "opacity-80",
      )}
    >
      <Link
        to="/marketplace"
        search={{ ...search, tab: "vendors", vendor: vendor.id }}
        aria-label={t("marketplace.vendors.openDetailAria", {
          name: vendor.name,
          defaultValue: "Open {{name}} details",
        })}
        className="absolute inset-0 z-0 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      />

      <CardHeader className="relative z-[1] flex flex-row items-start gap-3 px-4 pt-4 pb-3 pointer-events-none">
        <VendorMark name={vendor.name} logoUrl={vendor.logoUrl} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug tracking-[-0.01em] text-foreground [overflow-wrap:anywhere]">
              {vendor.name}
            </h3>
            {isConnected && (
              <Badge variant="status-connected" className="shrink-0">
                {t("marketplace.catalog.connected", "Connected")}
              </Badge>
            )}
            {paused && (
              <Badge variant="secondary" className="shrink-0">
                {t("marketplace.vendors.availability.paused", "Paused")}
              </Badge>
            )}
            {unavailable && (
              <Badge variant="secondary" className="shrink-0">
                {t("marketplace.vendors.availability.unavailable", "Unavailable")}
              </Badge>
            )}
          </div>
          {card.positioning && (
            <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">
              {card.positioning}
            </p>
          )}
        </div>
      </CardHeader>

      <CardContent className="relative z-[1] flex flex-1 flex-col gap-3 px-4 pb-3 pointer-events-none">
        {card.capabilities.length > 0 && (
          <ul className="flex flex-wrap gap-1.5">
            {card.capabilities.map((capability) => (
              <li key={capability}>
                <span className="inline-flex rounded-sm bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                  {t(`marketplace.vendors.capabilities.${capability}`, capability)}
                </span>
              </li>
            ))}
          </ul>
        )}

        <dl className="mt-auto grid grid-cols-3 gap-2 text-xs">
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-muted-foreground/80">
              {t("marketplace.vendors.card.priceFrom", "From")}
            </dt>
            <dd className="mt-0.5 font-semibold tabular-nums text-foreground">
              {card.priceFrom != null
                ? formatVendorPriceFrom(card.priceFrom, card.currency)
                : t("marketplace.vendors.card.priceOnRequest", "On request")}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-muted-foreground/80">
              {t("marketplace.vendors.card.turnaround", "Turnaround")}
            </dt>
            <dd className="mt-0.5 font-semibold tabular-nums text-foreground">
              {card.turnaroundDays != null
                ? t("marketplace.vendors.card.turnaroundDays", {
                    count: card.turnaroundDays,
                    defaultValue_one: "{{count}} day",
                    defaultValue_other: "{{count}} days",
                  })
                : t("marketplace.vendors.card.turnaroundUnknown", "—")}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-muted-foreground/80">
              {t("marketplace.vendors.card.onTime", "On-time")}
            </dt>
            <dd className="mt-0.5 font-semibold tabular-nums text-foreground">
              {card.metrics.onTimeRate != null
                ? t("marketplace.vendors.card.onTimeValue", {
                    value: Math.round(card.metrics.onTimeRate * 100),
                    defaultValue: "{{value}}%",
                  })
                : t("marketplace.vendors.card.metricPending", "New")}
            </dd>
          </div>
        </dl>
      </CardContent>

      <CardFooter className="relative z-[1] mt-auto gap-2 border-t border-border/60 bg-muted/15 px-4 py-2.5">
        {isConnected ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1 cursor-pointer"
              disabled={paused}
              onClick={() => onSendScripts(vendor.id)}
            >
              {t("marketplace.vendors.actions.sendScripts", "Send scripts")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="cursor-pointer"
              onClick={() => onManage(vendor.id)}
            >
              {t("marketplace.vendors.actions.manage", "Manage")}
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="cursor-pointer"
            disabled={!canConnect}
            onClick={() => onConnect(vendor.id)}
          >
            {t("marketplace.catalog.connect", "Connect")}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

function VendorMark({ name, logoUrl }: { name: string; logoUrl: string | null }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        loading="lazy"
        className="size-11 shrink-0 rounded-md border border-border/60 bg-background object-contain p-1"
      />
    );
  }

  return (
    <span
      aria-hidden
      style={{ backgroundColor: getAvatarColor(name) }}
      className="flex size-11 shrink-0 items-center justify-center rounded-md text-sm font-semibold text-foreground/80"
    >
      {getVendorInitials(name)}
    </span>
  );
}

export function VendorCatalogCardSkeleton() {
  return (
    <Card
      data-testid="vendor-card-skeleton"
      className="flex h-full min-h-[248px] flex-col gap-0 overflow-hidden py-0"
    >
      <CardHeader className="flex flex-row items-start gap-3 px-4 pt-4 pb-3">
        <Skeleton className="size-11 shrink-0 rounded-md" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-2/5" />
          <Skeleton className="h-3 w-full" />
        </div>
      </CardHeader>
      <CardContent className="flex-1 px-4 pb-3">
        <div className="flex gap-1.5">
          <Skeleton className="h-5 w-12 rounded-sm" />
          <Skeleton className="h-5 w-16 rounded-sm" />
        </div>
        <div className="mt-6 grid grid-cols-3 gap-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      </CardContent>
      <CardFooter className="mt-auto border-t border-border/60 bg-muted/15 px-4 py-2.5">
        <Skeleton className="h-8 w-20 rounded-md" />
      </CardFooter>
    </Card>
  );
}
